<template>
  <Skeleton v-if="!isAllDataReady" height="40px" width="100px" />
  <button
    v-else-if="showProfile"
    class="rail-btn-hover rail-btn-nowrap"
    aria-label="open account drawer"
    aria-haspopup="dialog"
    @click="handleOpenAccountSlideout"
  >
    <i class="icon-28x28 profile-nav-icon margin-right-xxs"></i>
    <AsideText :subtitle="stats" data-fs-mask
      ><span>Hi, {{ username }}</span></AsideText
    >
  </button>
  <div v-else class="relative sign-in-tooltip-space">
    <Link
      :href="Links.account.signIn.url"
      :tracking="Links.account.signIn.tracking"
      class="rail-btn-hover rail-btn-nowrap undecorated-link"
      @click="handleAccountLinkClick"
      @focusin="deactivateSignInTooltip"
    >
      <i class="icon-28x28 profile-nav-icon margin-right-xxs"></i>
      <span class="grid-x heavy-sm">Sign In</span>
    </Link>
    <SignInTooltip v-if="isSignInTooltipActive" class="sign-in-tooltip--enchanced">
      <template #default="{ suspendTimer, resumeTimer }">
        <span v-if="isMcom" class="small">See more of<br />what you love</span>
        <span v-else class="small">For the best<br />experience:</span>
        <a
          :href="Links.account.signIn.url"
          :class="{ primary: isMcom, secondary: !isMcom }"
          class="button button-small"
          @focusin="suspendTimer"
          @focusout="resumeTimer"
          @click="useSignInClick({ tooltipTags: true })"
        >
          Sign In
        </a>
      </template>
    </SignInTooltip>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Skeleton from '@nuxt-common/atomic-ui/app/components/skeleton/Skeleton';
import Link from '../common/Link';
import AsideText from './AsideText.common';
import SignInTooltip from '../link-rail/SignInTooltip.common';
import Links from '#shared/constants/Links';
import useAccount from '~/composables/useAccount';
import useLoyalty from '~/composables/useLoyalty';
import useAccountSlideout from '~/composables/features/useAccountSlideout';
import useSignInClick from '~/composables/useSignInClick';
import { useSignInTooltipActivator } from '~/composables/features/useSignInTooltip';
import { tagAccountLink } from '#shared/features/analytics/linkRailTags';
import { tagAccountDrawerOpen } from '#shared/features/analytics/accountTags';
import { onRewardsReady, onUserInit } from '~/events';

const { isMcom } = useEnvProps();
const pageData = usePageData();
const userReady = ref(false);
const rewardsReady = ref(false);
const { isSignedIn, username } = useAccount();
const { rewards, bothPoints, formatPrice } = useLoyalty();
const { openAccountSlideout } = useAccountSlideout();
const isAllDataReady = computed(() => {
  if (!userReady.value) {
    return false;
  }

  return isSignedIn.value ? rewardsReady.value : true;
});
const showProfile = computed(() => isSignedIn.value && rewardsReady.value);
const isDomestic = computed(() => pageData.navigation.context.isDomestic);
const { isSignInTooltipActive, deactivateSignInTooltip } = useSignInTooltipActivator({ isSignedIn, isDomestic });
const stats = computed(() => {
  const { balance } = rewards.value;
  if (isMcom) {
    if (balance) return `${formatPrice(balance, 'profile')} Star Money`;
    if (bothPoints.value) return `${bothPoints.value} points`;
    return '';
  }

  // bcom
  if (balance) return `${formatPrice(balance, 'profile')} Rewards`;

  return bothPoints.value
    ? bothPoints.value
        .split('/')
        .map((point) => parseInt(point, 10).toLocaleString('en-US'))
        .join('/')
    : '';
});

onUserInit(() => {
  userReady.value = true;
});

onRewardsReady(() => {
  rewardsReady.value = true;
});

const handleOpenAccountSlideout = () => {
  openAccountSlideout();
  tagAccountDrawerOpen();
};

function handleAccountLinkClick() {
  if (!isSignedIn.value) {
    useSignInClick();
  }
  tagAccountLink();
}
</script>

<style lang="scss">
.sign-in-tooltip--enchanced {
  top: 50%;
  right: 0;
  transform: translateY(-50%);

  &::before,
  &::after {
    display: none;
  }
}
</style>

<style lang="scss" brand="mcom">
@import '~/assets/scss/icons.mcom.scss';

.profile-nav-icon {
  @extend .profile-dark-large;
}

.sign-in-tooltip-space {
  margin-left: 95px;
}
</style>

<style lang="scss" brand="bcom">
@import '~/assets/scss/icons.bcom.scss';

.profile-nav-icon {
  @extend .profile;
  opacity: 0.42;
}

.sign-in-tooltip-space {
  margin-left: 100px;
}
</style>
